import { HttpClient, HttpErrorResponse, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { ClientProcessingError } from 'app/_errors/client-processing.error';
import { LoginAuthenticationError } from 'app/_errors/login-authentication.error';
import { RefreshAuthenticationError } from 'app/_errors/refresh-authentication.error';
import { UnknownApplicationError } from 'app/_errors/unknown-application.error';
import { CaptchaService } from 'app/_services/captcha.service';
import { DashboardService } from 'app/shared-pages/dashboard/dashboard.service';
import { CookieService } from 'ngx-cookie-service';
import { from, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { Observable } from 'rxjs/Rx';
import { environment } from '../../environments/environment';
import { Globals } from '../_globals';
import { ContractorService } from './contractor.service';
import { CryptoService } from './crypto.service';

@Injectable()
export class AuthService {
  private static readonly INVALID_ROLE_COMBINATION_ERROR_TYPE = 'invalid-role-combination';
  private invalidRoleCombinationIdentifier: string | null = null;
  helper = new JwtHelperService();
  cryptoService: CryptoService = new CryptoService();

  constructor(
    private http: HttpClient,
    private router: Router,
    public globals: Globals,
    private cookieService: CookieService,
    private dashboardService: DashboardService,
    private contractorService: ContractorService,
    private captchaService: CaptchaService
  ) {}

  public login(creds) {
    const headers = new HttpHeaders().set(environment.InterceptorSkipHeader, '');
    let deviceId = this.cookieService.get('deviceId_' + creds.username.toLowerCase());

    return new Observable((observer) => {
      if (deviceId) {
        creds.deviceId = this.cryptoService.decryptText(deviceId);
      }
      this.http
        .post(environment.apiUrl + 'api/authenticate', creds, { headers })
        .pipe((res: any) => {
          res.subscribe(
            (data) => {
              // tslint:disable-next-line:max-line-length
              const expirationDate = this.helper.getTokenExpirationDate(data.id_token);
              console.log('token expires', expirationDate);

              const credsExp = new Date(expirationDate);
              credsExp.setHours(expirationDate.getHours() + environment.credentialsExpiryHours);
              console.log('credentials expire', credsExp.toUTCString());

              localStorage.setItem(environment.keys.accessToken, this.cryptoService.encryptText(data.id_token).toString());
              localStorage.setItem(environment.keys.credentialsExpiry, this.cryptoService.encryptText(credsExp.toUTCString()).toString());
              localStorage.setItem(environment.keys.username, this.cryptoService.encryptText(creds.username).toString());
              localStorage.setItem(environment.keys.password, this.cryptoService.encryptText(creds.password).toString());
              localStorage.setItem(environment.keys.what3words, this.cryptoService.encryptText(data.w3).toString());
              localStorage.setItem(environment.keys.dynamoUserSessions, this.cryptoService.encryptText(data.dy).toString());

              if (data.deviceId) {
                const cookieExp = new Date();
                cookieExp.setFullYear(cookieExp.getFullYear() + 5);
                this.cookieService.set('deviceId_' + creds.username.toLowerCase(), this.cryptoService.encryptText(data.deviceId).toString(), cookieExp, undefined, undefined, true, 'Lax');
              }

              if (data.changePassword) {
                localStorage.setItem(environment.keys.changePasswordRequired, (data.changePassword));
              } else {
                localStorage.removeItem(environment.keys.changePasswordRequired);
              }
              console.log('Log-in successful');
              observer.next();
            },
            (error) => {
              if (
                error.error &&
                error.error.status === 401 &&
                (error.error.detail === 'One-time passcode required' ||
                error.error.detail.startsWith('One-time passcode sent') ||
                error.error.detail === 'One time pass code is incorrect' ||
                error.error.detail === 'One time pass code expired')
              ) {
                observer.error(error.error);
              } else if (error.error && error.error.errorKey === 'failedtosendtwiliomessage') {
                observer.error(error.error);
              } else if( error.error.status === 401 && error.error.detail === 'Too many incorrect password attempts - account locked') {
                observer.error(error.error)
              } else if (error instanceof HttpErrorResponse && error.error && error.error.status === 401) {
                throw new LoginAuthenticationError(error);
              } else {
                throw error;
              }
            }
          );

          return res;
        })
        .pipe(catchError(this.handleError));
    });
  }

  private storeGlobalUserAndOrg(user) {
    this.invalidRoleCombinationIdentifier = null;

    this.globals.user = {
      id: user.id,
      authorities: user.authorities,
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      langKey: user.langKey,
      login: user.login,
      signatureFile: user.signatureFile,
      isSseContext: false,
      canEditCompetencies: false,
      canEditTurbineCompetencies: false,
      canEditNonTurbineCompetencies: false,
    };
    this.globals.siteOwnerUsers = [];
    this.globals.siteOwnerToggles = {
      addAssets: false,
      allowRestrictionsWorkflow: false,
      allowWorkOrderScheduling: false,
      allowAssetScheduling: false,
      allowTocAcknowledgement: false,
    };

    if (user.workerUser) {
      this.globals.user.workerUserId = user.workerUser.id;
    }

    if (user.workerUser && user.workerUser.maintenance) {
      this.globals.contractor = {
        id: user.workerUser.maintenance.id,
        name: user.workerUser.maintenance.name,
      };
    }

    if (user.authorities.find((auth) => auth.name === 'ROLE_ADMIN')) {
      this.globals.user.role = 5;
      this.globals.user.routeRole = 'admin';
      this.globals.user.isSseContext = false;
      this.globals.user.canEditCompetencies = true;
      this.globals.user.canEditTurbineCompetencies = true;
      this.globals.user.canEditNonTurbineCompetencies = true;
    } else if (user.authorities.find((auth) => auth.name === 'ROLE_CALL_CENTER')) {
      this.globals.user.role = 4;
      this.globals.user.routeRole = 'support';
      this.globals.user.isSseContext = false;
      this.globals.user.canEditCompetencies = true;
      this.globals.user.canEditTurbineCompetencies = true;
      this.globals.user.canEditNonTurbineCompetencies = true;
    } else if (user.authorities.find((auth) => auth.name === 'ROLE_PS_MANAGER')) {
      this.globals.user.role = 10;
      this.globals.user.routeRole = 'power-station-manager';
      this.globals.powerStationOwner.mainSiteId = user.workerUser.psManagerSiteOwnerUser.mainSiteId;
      this.globals.powerStationOwner.skylarkUserId = user.workerUser.psManagerSiteOwnerUser.skylarkUserId;
      this.globals.siteOwnerUsers.push({
        skylarkUserId: user.workerUser.psManagerSiteOwnerUser.skylarkUserId,
        name: user.workerUser.psManagerSiteOwnerUser.name,
      });

    } else if (user.authorities.find((auth) => auth.name === 'ROLE_PS_OWNER') || user.authorities.find((auth) => auth.name === 'ROLE_PS_ADMIN')) {
      if(user.authorities.find((auth) => auth.name === 'ROLE_PS_OWNER')){
        this.globals.powerStationOwner.mainSiteId = user.siteOwnerUser.mainSiteId;
        this.globals.powerStationOwner.skylarkUserId = user.siteOwnerUser.skylarkUserId;
        this.globals.siteOwnerUsers.push({
          skylarkUserId: user.siteOwnerUser.skylarkUserId,
          name: user.siteOwnerUser.name,
        });
      } else {
        this.globals.powerStationOwner.mainSiteId = user.powerstationAdminUser.siteOwnerUser.mainSiteId;
        this.globals.powerStationOwner.skylarkUserId = user.powerstationAdminUser.siteOwnerUser.skylarkUserId;
        this.globals.siteOwnerUsers.push({
          skylarkUserId: user.powerstationAdminUser.siteOwnerUser.skylarkUserId,
          name: user.powerstationAdminUser.siteOwnerUser.name,
        });
      }
      user.authorities.push({ name: 'ROLE_PS_OWNER' });
      this.globals.user.role = 9;
      this.globals.user.routeRole = 'power-station-owner';
      this.globals.user.isSseContext = false;
      this.globals.user.canEditCompetencies = true;
      this.globals.user.canEditTurbineCompetencies = true;
      this.globals.user.canEditNonTurbineCompetencies = true;

      this.globals.siteOwnerToggles = {
        addAssets: false,
        allowRestrictionsWorkflow: false,
        allowWorkOrderScheduling: false,
        allowAssetScheduling: false,
        allowTocAcknowledgement: false,
      };
    } else if (user.authorities.find((auth) => auth.name === 'ROLE_SITE_OWNER')) {
      this.globals.user.role = 3;
      this.globals.user.routeRole = 'site-owner';
      this.globals.siteOwnerUsers.push({
        skylarkUserId: user.siteOwnerUser.skylarkUserId,
        name: user.siteOwnerUser.name,
      });
      this.globals.user.isSseContext = false;
      this.globals.user.canEditCompetencies = true;
      this.globals.user.canEditTurbineCompetencies = true;
      this.globals.user.canEditNonTurbineCompetencies = true;

      // site owner's feature toggles
      this.globals.siteOwnerToggles = {
        addAssets: user.siteOwnerUser.addAssets,
        allowRestrictionsWorkflow: user.siteOwnerUser.allowRestrictionsWorkflow,
        allowWorkOrderScheduling: user.siteOwnerUser.allowWorkOrderScheduling,
        allowAssetScheduling: user.siteOwnerUser.allowAssetScheduling,
        allowTocAcknowledgement: user.siteOwnerUser.allowTocAcknowledgement,
      };
    } else if (user.authorities.find((auth) => auth.name === 'ROLE_MAINTENANCE_MANAGER')) {
      this.globals.user.role = 2;
      this.globals.user.routeRole = 'contractor-manager';
      // Contractor managers should not have canEditCompetencies, which is full permission
      // to approve, add, remove competencies and approvals. They have limited access to edit competencies.
      this.globals.user.isSseContext = false;
      this.globals.user.canEditCompetencies = false;
      this.globals.user.canEditTurbineCompetencies = false;
      this.globals.user.canEditNonTurbineCompetencies = false;
      if (user.maintenanceManagerUser){
        this.globals.contractor.oem = user.maintenanceManagerUser.oem;
        this.globals.contractor.maintenanceId = user.maintenanceManagerUser.maintenanceId;
        if (user.contractorManagerDetails) {
          if (user.contractorManagerDetails && user.contractorManagerDetails.maintenanceSiteOwners) {
            this.globals.contractor.hasPowerstationRelation = user.contractorManagerDetails.maintenanceSiteOwners.some((mso) => {
              return mso.siteOwnerUser && mso.siteOwnerUser.mainSiteId;
            });
          }
        }
      }


    } else if (user.authorities.find((auth) => auth.name === 'ROLE_USER')) {
      // ROLE_USER routes:
      // ROLE_USER
      // ├─ ROLE_SITE_DELEGATE - site-delegate
      // │  ├─ ROLE_CONTROL_ADMIN
      // │  ├─ ROLE_REGIONAL_MANAGER
      // │  └─ (default)
      // │  └─ canEditCompetencies: true only if ROLE_AUTHORISING_OFFICER also present
      // ├─ ROLE_WORK_ORDER_ADMIN - worker-admin
      // └─ (default) - worker
      const hasSiteDelegateRole = user.authorities.some(auth => auth.name === 'ROLE_SITE_DELEGATE');
      const hasAuthorisingOfficerRole = user.authorities.some(auth => auth.name === 'ROLE_AUTHORISING_OFFICER');
      const delegateSiteOwners = Array.isArray(user.delegateSiteOwners) ? user.delegateSiteOwners : [];
      const isRegionalManager: boolean = user.authorities.some(auth => auth.name === 'ROLE_REGIONAL_MANAGER');
      const isControlAdmin: boolean = user.authorities.some(auth => auth.name === 'ROLE_CONTROL_ADMIN');
      const hasSseDelegateSiteOwner = delegateSiteOwners.some((owner) => owner.skylarkUserId === environment.SSE_OWNER_SKYLARK_ID);

      if (hasAuthorisingOfficerRole && !hasSiteDelegateRole) {
        throw this.buildInvalidRoleCombinationError(user);
      }

      if (hasSiteDelegateRole) {
        if (!delegateSiteOwners.length) {
          throw new Error('This account has the site delegate role, but no delegated sites are configured. Please contact Skylark support.');
        }

        this.globals.siteOwnerToggles = {
          addAssets: delegateSiteOwners.some(owner => owner.addAssets),
          allowRestrictionsWorkflow: delegateSiteOwners.some(owner => owner.allowRestrictionsWorkflow),
          allowWorkOrderScheduling: delegateSiteOwners.some(owner => owner.allowWorkOrderScheduling),
          allowAssetScheduling: delegateSiteOwners.some(owner => owner.allowAssetScheduling),
          allowTocAcknowledgement: delegateSiteOwners.some(owner => owner.allowTocAcknowledgement),
        };
        this.globals.siteOwnerUsers = delegateSiteOwners.map(owner => ({
            skylarkUserId: owner.skylarkUserId,
            name: owner.name,
        }));

        // Control Admin and Regional Manager both must also have Site Delegate role.
        // they follow site delegate route but have:
        //  control admin - ability to perform transfer of control function
        //  regional manager - ability to assign site delegate privilege
        if (isControlAdmin) {
          this.globals.user.role = 11;
        } else if (isRegionalManager) {
          this.globals.user.role = 12;
        } else {
          this.globals.user.role = 7;
        }

        this.globals.user.routeRole = 'site-delegate';

        if (isControlAdmin) {
          this.globals.user.isSseContext = hasSseDelegateSiteOwner;
          this.globals.user.canEditCompetencies = false;
          this.globals.user.canEditTurbineCompetencies = false;
          this.globals.user.canEditNonTurbineCompetencies = false;
        } else if (hasSseDelegateSiteOwner) {
          this.globals.user.isSseContext = true;
          this.globals.user.canEditCompetencies = true;
          this.globals.user.canEditTurbineCompetencies = false;
          this.globals.user.canEditNonTurbineCompetencies = true;
        } else {
          this.globals.user.isSseContext = false;
          this.globals.user.canEditCompetencies = true;
          this.globals.user.canEditTurbineCompetencies = true;
          this.globals.user.canEditNonTurbineCompetencies = true;
        }
      } else {
        this.globals.user.role = 1;
        this.globals.user.routeRole = 'worker';
        this.globals.user.isSseContext = false;
        this.globals.user.canEditCompetencies = false;
        this.globals.user.canEditTurbineCompetencies = false;
        this.globals.user.canEditNonTurbineCompetencies = false;
      }
    } else if (user.authorities.find((auth) => auth.name === 'ROLE_AUTHORISING_OFFICER')) {
      // AO for SSE, they will have no other ROLES.
      // non-SSE Delegates will also have the AO role, but this is taken care of in the block above and just means 'can edit competencies'
      // this block must come after 'Worker' role block.
      this.globals.user.role = 13;
      this.globals.user.routeRole = 'authorising-officer';
      this.globals.siteOwnerUsers.push({
        skylarkUserId: environment.SSE_OWNER_SKYLARK_ID,
        name: environment.SSE_SITE_OWNER_NAME,
      });
      this.globals.user.isSseContext = true;
      this.globals.user.canEditCompetencies = true;
      this.globals.user.canEditTurbineCompetencies = true;
      this.globals.user.canEditNonTurbineCompetencies = false;
    } else {
      this.globals.user.role = 0;
    }

    return this.globals;
}

  public logout() {
    document.body.classList.remove('admin-view-as-banner');
    this.dashboardService.clearCache();
    //logout service will blacklist token so it cannot be reused
    this.http.post(environment.apiUrl + 'api/logout-token', {}).subscribe({
      next: () => {
        console.log('Logout request successful');
        this.handleUiLogout();
      },
      error: (err) => {
        console.error('Logout error', err);
        this.handleUiLogout();
      }
    });
  }
  public handleUiLogout() {
        this.invalidRoleCombinationIdentifier = null;
        this.clearLocalStorageExceptUiOptions();
        this.router.navigate(['/signin']);
  }

  private clearLocalStorageExceptUiOptions() {
    const prefix = 'ui.options.';
    const removedKeys = {};
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key.startsWith(prefix)) {
        removedKeys[key] = localStorage.getItem(key);
      }
    }

    localStorage.clear();

    for (const key in removedKeys) {
      localStorage.setItem(key, removedKeys[key]);
    }
  }

  public refreshToken(creds) {
    let deviceId = this.cookieService.get('deviceId_' + creds.username.toLowerCase());
    if (deviceId) {
      creds.deviceId = this.cryptoService.decryptText(deviceId);
    }
    const headers = new HttpHeaders().set(environment.InterceptorSkipHeader, '');
    return from(this.captchaService.generateCaptchaToken('contactUs')).concatMap((captchaToken) => {
      creds.captchaToken = captchaToken;
      return this.http.post(environment.apiUrl + 'api/authenticate', creds, { headers });
    }).pipe(
      catchError((error) => {
        if (error instanceof HttpErrorResponse && error.error && error.error.status === 401) {
          return throwError(new RefreshAuthenticationError(error));
        }

        return throwError(error);
      })
    );
  }

  public isLoggedIn() {
    const loggedin = localStorage.getItem(environment.keys.accessToken);
    return loggedin;
  }

  public fetchUserAndUpdateIntoGlobals(): Observable<Globals> {
    return this.http.get(environment.apiUrl + 'api/ext-skylark-user/current')
      .pipe(map(this.handleData))
      .pipe(

        switchMap((user: any) => this.handleContractorManager(user)),
        map((user: any) => {
          return this.storeGlobalUserAndOrg(user);
        }),
        catchError(this.handleError)
      );
  }

  // if Contractor Manager, we need to get additional information
  private handleContractorManager(user: any): Observable<any> {
    if (user.authorities.find((auth) => auth.name === 'ROLE_MAINTENANCE_MANAGER')) {
      return this.contractorService.getContractorForCurrentUser()
        .pipe(
          map(extraData => ({ ...user, contractorManagerDetails: extraData })),
          catchError(() => of(user)) // Continue with original user data if the call fails
        );
    }
    return of(user); // No additional call needed
  }

  public changePassword(password) {
    return this.http
      .post(environment.apiUrl + 'api/account/change-password', password)
      .pipe(map(this.handleData))
      .pipe(catchError(this.handleError));
  }

  // HANDLE DATA & ERRORS FUNCTIONS

  private handleData(res: HttpResponse<any>) {
    const body = res;
    return body;
  }

  private buildInvalidRoleCombinationError(user) {
    return {
      type: AuthService.INVALID_ROLE_COMBINATION_ERROR_TYPE,
      userId: user.id,
      email: user.email,
      login: user.login,
      authorities: user.authorities.map(auth => auth.name),
    };
  }

  public isInvalidRoleCombinationError(error: any): boolean {
    return error && error.type === AuthService.INVALID_ROLE_COMBINATION_ERROR_TYPE;
  }

  public redirectToInvalidRoleCombinationError(error?: any): void {
    this.invalidRoleCombinationIdentifier = error && (error.email || error.login) || null;
    this.clearLocalStorageExceptUiOptions();
    this.router.navigate(['/invalid-roles-login-error']);
  }

  public consumeInvalidRoleCombinationIdentifier(): string | null {
    const identifier = this.invalidRoleCombinationIdentifier;
    this.invalidRoleCombinationIdentifier = null;
    return identifier;
  }

  private handleError = (error: any) => {
    if (error instanceof HttpErrorResponse) {
      return throwError(error);
    }

    if (this.isInvalidRoleCombinationError(error)) {
      return throwError(error);
    }

    if (error instanceof Error) {
      return throwError(
        new ClientProcessingError('The application could not process the server response.', error)
      );
    }

    return throwError(
      new UnknownApplicationError('The application threw an unknown error value.', error)
    );
  }
}
